//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

// Where the keyboard goes when a tour ends. Leaving it where the tooltip
// stood is not an option: that pane unmounts, and a browser answers the
// removal of the focused element by falling back to the body, which starts
// the next Tab at the top of the page.

// Give the element the keyboard, making it focusable first if it will not
// take it otherwise. Asked of the element rather than decided from its tag,
// so nothing has to keep a list of what is focusable in step with the DOM.
export const focusForExit = (
  element: Element | null | undefined
): boolean => {
  if (!(element instanceof HTMLElement) || !element.isConnected)
    return false;

  element.focus();
  if (document.activeElement === element)
    return true;

  // An element carrying its own index has already said how it takes focus,
  // so the refusal is about something else -- disabled, hidden, inert --
  // which an index of ours would not fix. It would only cost the element
  // its place in the tab order.
  if (element.hasAttribute('tabindex'))
    return false;

  // A panel, a toolbar, a heading: the step pointed at it, so it is where
  // the user belongs, but it is not a control and takes no focus on its
  // own. A negative index makes it reachable by script without putting it
  // in the tab order, where it would become a stop for every later user.
  element.tabIndex = -1;
  element.focus();
  if (document.activeElement === element)
    return true;

  // Refused even so, which leaves nothing to keep the index for. A control
  // only briefly unfocusable -- disabled while its request is in flight --
  // would otherwise carry a -1 out of that state and be gone from the tab
  // order for good.
  element.removeAttribute('tabindex');
  return false;
};

// The first candidate that takes the keyboard wins. Falling off the end
// leaves focus exactly where it was, which is what the tour did before any
// of this and is no worse than the alternative.
export const restoreTourFocus = (
  candidates: Array<Element | null | undefined>
): boolean => candidates.some(focusForExit);
